<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import ExportScheduleDO = App.Data.ExportScheduler.ExportScheduleDO;
import ConfigureDataTableExport from "@/modules/scheduler/components/ConfigureDataTableExport.vue";
import ConfigureAlertTrigger from "@/modules/scheduler/components/ConfigureAlertTrigger.vue";
import { titleInputRules } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import ConfigureTiming from "@/ui/components/ConfigureTiming.vue";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import ConfigureSegmentExport from "@/modules/scheduler/components/ConfigureSegmentExport.vue";
import ExportDataTableDO = App.Data.ExportScheduler.ExportDataTableDO;
import ExportSegmentDO = App.Data.ExportScheduler.ExportSegmentDO;
import ConfigureDashboardExport from "@/modules/scheduler/components/ConfigureDashboardExport.vue";
import ExportDashboardDO = App.Data.ExportScheduler.ExportDashboardDO;
import Switch from "@/ui/components/switch/switch.vue";
import { onBeforeRouteLeave } from "vue-router";
import { unsavedChangesDialog } from "@/ui/composables/useUnsavedChangesDialog";
import { toRawDeep } from "@/Utils/to-raw-deep";
import isEqual from "lodash/isEqual";

const { t } = useI18n();
const props = defineProps<{
	exportSchedule: ExportScheduleDO;
}>();
const { exportSchedule } = toRefs(props);
const isGeneralConfigurationFormValid = ref(false);
const isSaving = ref(false);

// Initialise the selected trigger alerts from the loaded relation.
exportSchedule.value.alert_ids ??= (exportSchedule.value.alerts ?? []).map(
	(alert) => alert.id
);

const isAlertBased = computed({
	get: () => exportSchedule.value.trigger_type === "alert_based",
	set: (value: boolean) => {
		exportSchedule.value.trigger_type = value ? "alert_based" : "time_based";
		if (!value) {
			exportSchedule.value.alert_ids = [];
		}
	},
});

const savedExportSchedule = ref<ExportScheduleDO>(
	structuredClone(toRawDeep(exportSchedule.value))
);

const saveExportSchedule = async () => {
	isSaving.value = true;
	try {
		const result = await toastApiResult(
			useApiClient("exportSchedules").update(exportSchedule.value)
		);
		if (result.isOk()) {
			savedExportSchedule.value = structuredClone(
				toRawDeep(exportSchedule.value)
			);
		}
	} finally {
		isSaving.value = false;
	}
};

const getExportTypeIcon = (type) => {
	const iconMap = {
		data_table: "mdi-table",
		segment: "mdi-segment",
		dashboard: "mdi-view-dashboard-outline",
	};
	return iconMap[type] || "mdi-file-export";
};

const exportableHasFileType = computed(() => {
	return "file_type" in exportSchedule.value.exportable;
});

// An export can only be activated once it has a data source configured;
// activating without one fails when the export job runs.
const hasConfiguredSource = computed(() => {
	const exportable = exportSchedule.value.exportable;
	if ("stored_document_id" in exportable) {
		return exportable.stored_document_id !== null;
	}
	if ("segment_id" in exportable) {
		return exportable.segment_id !== null;
	}
	if ("dashboard_id" in exportable) {
		return exportable.dashboard_id !== null;
	}
	return false;
});

const isDirty = computed(
	() => !isEqual(exportSchedule.value, savedExportSchedule.value)
);

onBeforeRouteLeave((to, from, next) => {
	unsavedChangesDialog("export schedule", isDirty.value, next);
});
</script>

<template>
	<v-form
		v-model="isGeneralConfigurationFormValid"
		class="export-scheduler-form"
	>
		<div class="d-flex flex-column ga-2 pa-4">
			<!-- General Configuration Section -->

			<div class="d-flex align-center ga-1">
				<v-icon
					:icon="getExportTypeIcon(exportSchedule?.export_type.name_clean)"
					size="x-small"
				/>
				<span class="font-weight-medium text-subtitle-2">
					Export Configuration
				</span>
			</div>

			<v-text-field
				v-model="exportSchedule.title"
				:rules="titleInputRules(t)"
				label="Export Name"
				variant="filled"
				density="comfortable"
				hide-details="auto"
				clearable
			/>

			<v-select
				v-if="exportableHasFileType"
				v-model="exportSchedule.exportable.file_type"
				label="Export File Type"
				:items="['csv', 'xlsx']"
				variant="filled"
				density="comfortable"
				hide-details="auto"
			/>

			<!-- Export Type Specific Configuration -->

			<configure-data-table-export
				v-if="exportSchedule?.export_type.name_clean === 'data_table'"
				v-model="exportSchedule.exportable as ExportDataTableDO"
			/>

			<configure-segment-export
				v-if="exportSchedule?.export_type.name_clean === 'segment'"
				v-model="exportSchedule.exportable as ExportSegmentDO"
			/>

			<configure-dashboard-export
				v-if="exportSchedule?.export_type.name_clean === 'dashboard'"
				v-model="exportSchedule.exportable as ExportDashboardDO"
			/>
			<!-- Trigger Configuration -->

			<div class="d-flex align-center ga-1 mt-4">
				<v-icon icon="mdi-calendar-clock" size="x-small" />
				<span class="font-weight-medium text-subtitle-2">Trigger Settings</span>
			</div>

			<v-select
				v-model="isAlertBased"
				:items="[
					{
						title: 'On a schedule',
						value: false,
						props: { prependIcon: 'mdi-clock-outline' },
					},
					{
						title: 'When an alert fires',
						value: true,
						props: { prependIcon: 'mdi-bell-ring-outline' },
					},
				]"
				label="Trigger type"
				variant="filled"
				density="comfortable"
				hide-details="auto"
			/>

			<configure-timing
				v-if="!isAlertBased"
				v-model:interval-type="exportSchedule.execution_interval_type"
				v-model:interval-specification="
					exportSchedule.execution_interval_specification
				"
			/>

			<configure-alert-trigger v-else v-model="exportSchedule.alert_ids" />

			<!-- Action Buttons -->
			<div class="d-flex mt-5 align-center">
				<v-spacer />
				<v-tooltip
					:disabled="hasConfiguredSource"
					text="Configure a data source before activating this export"
					location="top"
				>
					<template #activator="{ props: tooltipProps }">
						<div v-bind="tooltipProps">
							<Switch
								v-model="exportSchedule.is_active"
								:disabled="!hasConfiguredSource"
								hide-details
								density="compact"
								label="Active"
								data-testid="scheduler-active-switch"
							/>
						</div>
					</template>
				</v-tooltip>
				<v-divider vertical class="mx-4" />
				<v-badge
					:model-value="isDirty"
					color="error"
					offset-x="4"
					offset-y="4"
					dot
				>
					<v-btn
						:loading="isSaving"
						:disabled="!isGeneralConfigurationFormValid"
						@click="saveExportSchedule"
						color="primary"
						height="40"
						variant="flat"
					>
						Save Export
					</v-btn>
				</v-badge>
			</div>
		</div>
	</v-form>
</template>
